import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Checkbox, Form, Input, Modal, Typography } from 'antd';
import { TbArrowRight, TbMail } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';
import type { DeletionVerificationRequestResult } from '@/types';

export type { DeletionVerificationRequestResult } from '@/types';

interface VerifiedDestructiveActionFormValues {
    acknowledged: boolean;
    verificationCode: string;
}

export interface VerifiedDestructiveActionModalProps {
    acknowledgementLabel: ReactNode;
    actionLabel: string;
    alertDescription: ReactNode;
    alertTitle?: ReactNode;
    children?: ReactNode;
    emailAddress: string;
    errorMessage?: string;
    onCancel: () => void;
    onConfirm: (verificationCode: string) => Promise<unknown>;
    onRequestCode: () => Promise<DeletionVerificationRequestResult>;
    open: boolean;
    title: ReactNode;
}

/** High-friction, email-verified confirmation for account- and workspace-wide deletion. */
const VerifiedDestructiveActionModal: FC<VerifiedDestructiveActionModalProps> = ({
    acknowledgementLabel,
    actionLabel,
    alertDescription,
    alertTitle = 'This action permanently deletes data',
    children,
    emailAddress,
    errorMessage = 'Unable to complete this deletion',
    onCancel,
    onConfirm,
    onRequestCode,
    open,
    title,
}) => {
    const { token } = useAntDesignTheme();
    const [form] = Form.useForm<VerifiedDestructiveActionFormValues>();
    const [requestState, setRequestState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [submitting, setSubmitting] = useState(false);
    const requestStartedRef = useRef(false);
    const verificationCode = Form.useWatch('verificationCode', form);
    const acknowledged = Form.useWatch('acknowledged', form);
    const busy = requestState === 'sending' || submitting;
    const canDelete = requestState === 'sent' && /^\d{8}$/.test(verificationCode ?? '') && acknowledged === true;

    const requestCode = useCallback(async () => {
        try {
            setRequestState('sending');
            const result = await onRequestCode();
            setRequestState('sent');
            toast.success(result.reused ? 'Deletion Code Already Sent' : 'Deletion Verification Code Sent', {
                description: `Check ${emailAddress}. The code expires in 10 minutes.`,
            });
        } catch (error) {
            captureException(error, { tags: { action: 'request-deletion-verification' } });
            setRequestState('error');
            toast.error('Unable to Send Deletion Code', {
                description: error instanceof Error ? error.message : 'Please try again later.',
            });
        }
    }, [emailAddress, onRequestCode]);

    useEffect(() => {
        if (!open) {
            form.resetFields();
            requestStartedRef.current = false;
            setRequestState('idle');
            setSubmitting(false);
            return;
        }

        if (!requestStartedRef.current) {
            requestStartedRef.current = true;
            void requestCode();
        }
    }, [form, open, requestCode]);

    const close = () => {
        if (!busy) {
            onCancel();
        }
    };

    const submit = async ({ verificationCode: submittedCode }: VerifiedDestructiveActionFormValues) => {
        if (!canDelete || busy) {
            return;
        }

        try {
            setSubmitting(true);
            await onConfirm(submittedCode);
            onCancel();
        } catch (error) {
            captureException(error, { tags: { action: 'verified-destructive-modal-confirm' } });
            toast.error(errorMessage, {
                description: error instanceof Error ? error.message : 'Verify the code and try again.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            destroyOnHidden
            footer={
                <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
                    <Button disabled={busy} onClick={close}>
                        Cancel
                    </Button>
                    <Button
                        danger
                        disabled={!canDelete || busy}
                        loading={submitting}
                        type='primary'
                        onClick={() => form.submit()}
                    >
                        {actionLabel}
                        <TbArrowRight size='1rem' />
                    </Button>
                </div>
            }
            keyboard={!busy}
            maskClosable={!busy}
            onCancel={close}
            open={open}
            title={title}
            width='min(30rem, calc(100vw - 2rem))'
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: token.margin }}>
                {children}
                <PlatformAlert description={alertDescription} showIcon status='error' title={alertTitle} />
                <Form<VerifiedDestructiveActionFormValues>
                    form={form}
                    initialValues={{ acknowledged: false, verificationCode: '' }}
                    layout='vertical'
                    requiredMark={false}
                    onFinish={(values) => void submit(values)}
                >
                    <Form.Item
                        label={
                            <span>
                                Enter the deletion code sent to <code>{emailAddress}</code>
                            </span>
                        }
                        name='verificationCode'
                        rules={[
                            { required: true, message: 'Enter the deletion code.' },
                            { len: 8, message: 'Enter the eight-digit deletion code.' },
                        ]}
                    >
                        <Input
                            autoComplete='one-time-code'
                            disabled={requestState !== 'sent' || submitting}
                            inputMode='numeric'
                            maxLength={8}
                            placeholder='Enter The Deletion Code...'
                            prefix={<TbMail size='1rem' style={{ color: token.colorTextTertiary }} />}
                        />
                    </Form.Item>
                    {requestState === 'error' ? (
                        <Button block disabled={busy} style={{ marginBottom: token.margin }} onClick={requestCode}>
                            Send a New Code
                        </Button>
                    ) : null}
                    <Form.Item
                        name='acknowledged'
                        rules={[
                            {
                                validator: (_, value: boolean) =>
                                    value ? Promise.resolve() : Promise.reject(new Error('Acknowledge the deletion.')),
                            },
                        ]}
                        valuePropName='checked'
                        style={{ marginBottom: 0 }}
                    >
                        <Checkbox disabled={busy}>
                            <Typography.Text>{acknowledgementLabel}</Typography.Text>
                        </Checkbox>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default VerifiedDestructiveActionModal;
